import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { UserMinus, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "src/components/ui/Button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/Table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/Dialog";

const RemoveUserFromSession = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const [userToRemove, setUserToRemove] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    setTitle("Remove User from Session");
    setDescription("Remove participants from the tutoring session");
    setActions(
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/admin/sessions")}
        className="flex items-center gap-1"
      >
        <ArrowLeft size={16} />
        Back to Sessions
      </Button>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  // Mock session data
  const session = {
    id: "123",
    subject: "Mathematics",
    date: "2023-12-15",
    time: "14:00",
    tutor: "John Smith",
  };

  // Mock participants data
  const participants = [
    {
      id: "1",
      name: "Alice Johnson",
      email: "alice@example.com",
      role: "Student",
    },
    {
      id: "2",
      name: "Bob Williams",
      email: "bob@example.com",
      role: "Student",
    },
    {
      id: "3",
      name: "Carol Davis",
      email: "carol@example.com",
      role: "Student",
    },
  ];

  const openConfirmDialog = (user) => {
    setUserToRemove(user);
    setConfirmDialogOpen(true);
  };

  const handleRemoveUser = () => {
    console.log("Removing user from session:", userToRemove);
    setConfirmDialogOpen(false);
    // In a real app, you would make an API call here
    // Then navigate back or refresh the list
    navigate("/admin/sessions");
  };

  return (
    <>
      <div className="space-y-6">
        <Card>
            <CardHeader>
              <CardTitle>Session Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Subject</p>
                  <p>{session.subject}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Tutor</p>
                  <p>{session.tutor}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p>{session.date}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p>{session.time}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <UserMinus className="mr-2 h-5 w-5 text-rfpurple-600" />
                Current Participants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {participants.map((participant) => (
                    <TableRow key={participant.id}>
                      <TableCell>{participant.name}</TableCell>
                      <TableCell>{participant.email}</TableCell>
                      <TableCell>{participant.role}</TableCell>
                      <TableCell>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openConfirmDialog(participant)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => navigate("/admin/sessions")}
                className="ml-auto"
              >
                Back to Sessions
              </Button>
            </CardFooter>
          </Card>

          <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
                  Confirm Removal
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to remove {userToRemove?.name} from this
                  session? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleRemoveUser}>
                  Remove User
                </Button>
              </DialogFooter>
            </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default RemoveUserFromSession;
